/* eslint-disable camelcase */
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { getDeviceId, identify as amplitudeIdentify, Identify } from '@amplitude/analytics-browser';
import { isBrowser } from '@firsttable/functions';
import { Client } from '@amplitude/experiment-js-client';
import { useRouter } from 'next/router';
import { useSessionStorage } from 'usehooks-ts';
import { useAuthState } from '@/components/context/AuthContext';
import { getString } from '@/lib/storage/sessionStorage';
import { runConsentedAmplitudeOperation } from '@/lib/analytics/amplitude/client';
import { useAmplitudeEnabled } from '@/lib/analytics/amplitude/useAmplitudeEnabled';
import { getExperiment } from '@/lib/analytics/amplitude/amplitudeExperiment';
import { getPermissionsListFromToken } from '../../utils/getPermissionsListFromToken';
import Logger from '../../../../lib/logger';

const logger = new Logger('AnalyticsContext');
export const TrackContext = React.createContext({});

export const AnalyticsContext = createContext<Client | undefined>(undefined);

type AnalyticsProviderProps = {
  children: React.ReactNode;
};

const resolveCityFromPath = (pathname: string): string | null => {
  const [firstPathSegment = ''] = pathname.split('?')[0].split('/').filter(Boolean);

  if (!firstPathSegment || firstPathSegment === 'book' || firstPathSegment === 'booking') {
    return null;
  }

  return firstPathSegment;
};

const deserializeCity = (value: string): string | null => {
  if (value === 'null') {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value);
    return typeof parsedValue === 'string' || parsedValue === null ? parsedValue : value;
  } catch {
    return value;
  }
};

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const amplitudeEnabled = useAmplitudeEnabled();
  const anonymousId = amplitudeEnabled ? getDeviceId() : undefined;
  const router = useRouter();
  const { user, isLoggedIn, state } = useAuthState();
  const { token } = state;
  const loggedIn = isLoggedIn || user?.isLoggedIn === true;
  const initialCity = isBrowser
    ? (resolveCityFromPath(window.location.pathname) ?? getString('last-city'))
    : null;
  const [city, setCity] = useSessionStorage<string | null>('last-city', initialCity, {
    deserializer: deserializeCity,
  });

  useEffect(() => {
    if (!isBrowser) {
      return;
    }

    const cityFromPath = resolveCityFromPath(window.location.pathname);
    if (!cityFromPath) {
      return;
    }

    setCity(cityFromPath);
  }, [router.asPath]);

  /** set user attributes */
  const attributes = useMemo(() => {
    const permissions = getPermissionsListFromToken(token);

    return {
      device_id: anonymousId,
      anonymous_id: anonymousId,
      coalesced_id: user?.id ?? anonymousId,
      logged_in: loggedIn,
      contact_permission: user?.contactPermission ?? undefined,
      city_name: city,
      action_source: 'website',
      site_id: process.env.NEXT_PUBLIC_SITE_ID,
      is_dev_mode: process.env.NODE_ENV !== 'production',
      path_name: router.pathname,
      as_path: router.asPath,
      permissions,
      has_expiring_credits: (user?.expiringCredit?.edges?.length ?? 0) > 0,
    };
  }, [
    anonymousId,
    city,
    router.asPath,
    router.pathname,
    token,
    user?.id,
    user?.contactPermission,
    user?.expiringCredit?.edges?.length,
    loggedIn,
  ]);

  /** fetch experiment data */
  const experiment = useMemo(() => getExperiment(amplitudeEnabled), [amplitudeEnabled]);

  useEffect(() => {
    if (!amplitudeEnabled) {
      return;
    }

    (async () => {
      try {
        await experiment.fetch({ user_properties: attributes });
      } catch (error) {
        logger.error('Error fetching experiment & user permissions data', error);
      }
    })();
  }, [attributes, amplitudeEnabled, experiment]);

  useEffect(() => {
    if (!amplitudeEnabled) {
      return;
    }

    const identifyObj = new Identify();
    Object.keys(attributes).forEach((key) => {
      const value = attributes[key as keyof typeof attributes];
      if (![undefined, null].includes(value)) {
        identifyObj.set(key, value);
      }
    });

    identifyObj.set('browser', navigator.userAgent);
    identifyObj.set('domain', new URL(window.location.href).hostname);
    identifyObj.set('window_width', window.innerWidth);

    runConsentedAmplitudeOperation(() => {
      // The queued payload is built before init. Read the device id when the operation runs.
      const coalescedId = user?.id ?? getDeviceId();
      if (coalescedId != null) {
        identifyObj.set('coalesced_id', coalescedId);
      }
      amplitudeIdentify(identifyObj);
    });
  }, [attributes, amplitudeEnabled]);

  return <AnalyticsContext.Provider value={experiment}>{children}</AnalyticsContext.Provider>;
};

export const useExperiment = () => {
  const experiment = useContext(AnalyticsContext);
  if (!experiment) {
    throw new Error('ExperimentContext not found');
  }
  return experiment;
};
